import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';

// Invite member
export const inviteMember = createAsyncThunk(
  'group/inviteMember',
  async (inviteData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/subscriptions/invite', inviteData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const fetchGroupData = createAsyncThunk(
  'group/fetchGroupData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/subscriptions/my-group');
      if (!response.data || !response.data.data) {
        throw new Error('Invalid response format');
      }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const acceptInvitationThunk = createAsyncThunk(
  'group/acceptInvitation',
  async (payload, { rejectWithValue }) => {
    try {
      // Support both string and object for backward compatibility
      let token, forceSwitch;
      if (typeof payload === 'string') {
        token = payload;
        forceSwitch = false;
      } else {
        token = payload.token;
        forceSwitch = payload.forceSwitch || false;
      }
      const response = await apiClient.post(
        '/subscriptions/accept-invitation',
        { token, forceSwitch });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
export const removeInvitationThunk = createAsyncThunk(
  'group/removeInvitation',
  async (invitationId, { rejectWithValue }) => {
    try {
      
      const response = await apiClient.delete(`/subscriptions/members/${invitationId}`);

      return { invitationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const removeMemberThunk = createAsyncThunk(
  'group/removeMember',
  async ({ memberEmail, canvasId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`/subscriptions/members/${memberEmail}`, {
        data: { canvasId } // Send canvasId in request body
      });
      return { memberEmail, canvasId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

//fetch user invitations
export const fetchUserInvitations = createAsyncThunk(
  'group/fetchUserInvitations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/subscriptions/invitations');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Add new thunk for rejecting invitations
export const rejectInvitationThunk = createAsyncThunk(
  'group/rejectInvitation',
  async (invitationId, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/subscriptions/invitations/${invitationId}/reject`);
      return { invitationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
//Create Kids Account
export const createKidAccount = createAsyncThunk(
  'group/createKidAccount',
  async (kidData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/subscriptions/members/kid', kidData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateKidAccount = createAsyncThunk(
  'group/updateKidAccount',
  async ({ kidId, updateData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/subscriptions/members/kid/${kidId}`, updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// update member
export const updateMember = createAsyncThunk(
  'group/updateMember',
  async ({ email, updateDto }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/subscriptions/members/${email}`, updateDto);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// verify invitation
export const verifyInvitationThunk = createAsyncThunk(
  'group/verifyInvitation',
  async (token, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/subscriptions/invitations/verify?token=${token}`); 
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const groupSlice = createSlice({
  name: 'group',
  initialState: {
    members: [],
    invites: [],
    receivedInvites: [],
    owner: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(inviteMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(inviteMember.fulfilled, (state, action) => {
        state.loading = false;
        state.members = [...state.members, action.payload.data];
      })
      .addCase(inviteMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
    .addCase(fetchGroupData.pending, (state) => {
      state.loading = true;
    })
    .addCase(fetchGroupData.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.owner = action.payload.owner || null;
        state.members = Array.isArray(action.payload.members) ? action.payload.members : [];
        state.invites = Array.isArray(action.payload.invites) ? action.payload.invites : [];
      }
    })
    .addCase(fetchGroupData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })
    // Accept invitation cases
    .addCase(acceptInvitationThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(acceptInvitationThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.invites = state.invites.map(invite => 
      invite.token === action.meta.arg ? { ...invite, status: 'accepted' } : invite
      );
    })
    .addCase(acceptInvitationThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })

    // Remove invitation cases
    .addCase(removeInvitationThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeInvitationThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.invites = state.invites.filter(invite => invite._id !== action.meta.arg);
    })
    .addCase(removeInvitationThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })
    // Remove member cases
    .addCase(removeMemberThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeMemberThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.members = state.members.filter(member => member._id !== action.meta.arg);
    })
    .addCase(removeMemberThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })
    //fetch user invitations
    .addCase(fetchUserInvitations.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchUserInvitations.fulfilled, (state, action) => {
      state.loading = false;
      // Ensure we're setting an array
      state.receivedInvites = Array.isArray(action.payload.data) ? action.payload.data : [];
    })
    .addCase(fetchUserInvitations.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })

    // rejecting invitations
    .addCase(rejectInvitationThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(rejectInvitationThunk.fulfilled, (state, action) => {
      state.loading = false;
      state.receivedInvites = state.receivedInvites.filter(
        invite => invite._id !== action.payload.invitationId
      );
    })
    .addCase(rejectInvitationThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })
    // Verify invitation cases
    .addCase(verifyInvitationThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(verifyInvitationThunk.fulfilled, (state, action) => {
      state.loading = false;
      // Handle successful verification
    })
    .addCase(verifyInvitationThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export default groupSlice.reducer;